import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Check, Loader2, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { track } from "@/lib/analytics";
import { getPracticePlans, getPracticeAccess, fmtDate, rememberAfterLogin } from "@/lib/practiceAccess";
import { buyPass } from "@/lib/razorpayPass";
import PayPalCheckout from "@/components/PayPalCheckout";

const money = (p) => (p.currency === "USD" ? "$" : "₹") + p.price;
const perDay = (p) => (p.currency === "USD" ? "$" + p.per_day.toFixed(2) : "₹" + Number(p.per_day.toFixed(p.per_day < 10 ? 1 : 0)));

const WHAT_YOU_GET = [
  "Practice every shot as often as you like",
  "A voice coach that remembers your session, with a drill for what you keep missing",
  "Runs on your phone: your video isn't uploaded",
];

// What a buyer outside India sees for a pass on the PayPal screen (see PayPalCheckout).
function passItemFor(plan, mode) {
  return {
    kind: "pass", key: plan.key, plan, mode,
    name: `Practice pass \u00b7 ${plan.label}`,
    sub: `about $${plan.per_day.toFixed(2)} a day \u00b7 one payment, no auto-renewal`,
    price: plan.price, badge: plan.highlight ? "Most popular" : "Practice pass",
    perks: [...WHAT_YOU_GET, "Opens as soon as PayPal confirms; buying again adds days to a running pass"],
  };
}

/**
 * The plan cards, one tap to pay. Used in the paywall dialog and on the pricing page.
 * `onPaid(access)` runs once the pass is open.
 */
export function PracticePlans({ source, onPaid, onCheckoutOpen, onCheckoutEnd, className = "" }) {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(false);
  const [paypalPlan, setPaypalPlan] = useState(null);   // set while a buyer outside India is on the PayPal screen
  const [paypalMode, setPaypalMode] = useState("link");
  useEffect(() => { getPracticePlans().then((d) => { setPlans(d.plans || []); setPaypalMode(d.paypal_mode || "link"); }).catch(() => setError(true)); }, []);

  const onPaypalPaid = async (res) => {
    const plan = paypalPlan;
    track("practice_pass_purchased", { plan: plan?.key, price: plan?.price, currency: "USD", source, provider: "paypal" });
    let access = null;
    try { access = await getPracticeAccess(); } catch { /* the pass is open on the server; the page refreshes its own view */ }
    const until = access?.pass_until || res?.pass_until;
    toast.success(until ? `You're in. Practice is open until ${fmtDate(until)}.` : "You're in. Practice is open.");
    setPaypalPlan(null);
    onPaid?.(access);
  };

  const buy = async (plan) => {
    if (!isAuthenticated) {
      rememberAfterLogin(`${window.location.pathname}${window.location.search}`);
      toast("Create a free account first, then pick your pass");
      navigate("/auth");
      return;
    }
    if (plan.provider === "paypal") {   // outside India: the PayPal screen, no Razorpay
      track("practice_checkout_started", { plan: plan.key, price: plan.price, currency: plan.currency, source, provider: "paypal" });
      setPaypalPlan(plan);
      return;
    }
    setBusy(plan.key);
    track("practice_checkout_started", { plan: plan.key, price: plan.price, currency: plan.currency, source });
    // onCheckoutOpen: the paywall dialog steps aside while Razorpay is open (see buyPass); onCheckoutEnd: it comes back
    // if the buyer cancels or the payment fails
    const r = await buyPass(plan, { onOpening: () => onCheckoutOpen?.() });
    setBusy(null);
    if (r.status !== "paid" && r.status !== "redirected") onCheckoutEnd?.();
    if (r.status === "paid") {
      track("practice_pass_purchased", { plan: plan.key, price: plan.price, currency: plan.currency, source });
      toast.success(r.access?.pass_until ? `You're in. Practice is open until ${fmtDate(r.access.pass_until)}.` : "You're in. Practice is open.");
      onPaid?.(r.access);
    } else if (r.status === "redirected") {
      track("practice_checkout_redirect_dodo", { plan: plan.key, price: plan.price, currency: plan.currency, source });
      // if the browser swallows the redirect, a tap on this still gets the buyer to the checkout
      toast.message("Taking you to secure checkout…", { duration: 20000, action: { label: "Continue", onClick: () => window.location.assign(r.url) } });
    } else if (r.status === "cancelled") {
      track("practice_checkout_cancelled", { plan: plan.key, source });
    } else if (r.status === "unconfirmed") {
      track("practice_checkout_unconfirmed", { plan: plan.key, source });
      toast.error(`Your payment went through (ref ${r.ref}) but we couldn't open your pass yet. It will open on its own within a few minutes; reload then. If it doesn't, contact support with that ref.`, { duration: 20000 });
    } else {
      track("practice_checkout_failed", { plan: plan.key, source });
      toast.error(r.message || "Payment failed");
    }
  };

  if (error) return <p className={`text-sm text-zinc-400 ${className}`}>Couldn't load the plans. Check your connection and try again.</p>;
  if (!plans) return <div className={`flex justify-center py-8 ${className}`}><Loader2 className="w-5 h-5 animate-spin text-zinc-500" /></div>;
  if (paypalPlan) {
    return (
      <div className={className}>
        <PayPalCheckout item={passItemFor(paypalPlan, paypalMode)} source={source} onBack={() => setPaypalPlan(null)} onPaid={onPaypalPaid} />
      </div>
    );
  }
  const cheapest = Math.min(...plans.map((p) => p.per_day));
  return (
    <div className={`grid gap-3 sm:grid-cols-3 ${className}`}>
      {plans.map((p) => {
        const best = p.per_day === cheapest && plans.length > 1;
        return (
          <button key={p.key} type="button" onClick={() => buy(p)} disabled={!!busy}
            className={`relative text-left rounded-2xl border px-4 py-3 sm:p-4 min-h-[76px] sm:min-h-[112px] flex items-center justify-between gap-3 sm:block transition-colors disabled:opacity-60 ${p.highlight ? "border-lime-400/60 bg-lime-400/10" : "border-zinc-700 bg-zinc-900 hover:border-zinc-500"}`}>
            {(p.highlight || best) && (
              <span className="absolute -top-2.5 left-3 text-[10px] font-black uppercase tracking-wide bg-lime-400 text-black rounded px-1.5 py-0.5">{p.highlight ? "Most popular" : "Best value"}</span>
            )}
            <span className="block min-w-0">
              <span className="block text-[13px] font-semibold text-zinc-300">{p.label} <span className="font-normal text-zinc-500">· about {perDay(p)} a day{p.provider === "dodo" ? " · tax included" : ""}</span></span>
              <span className="block font-heading font-black text-3xl text-white mt-0.5">{busy === p.key ? <Loader2 className="w-6 h-6 animate-spin" /> : money(p)}</span>
            </span>
            <span className="shrink-0 sm:block sm:mt-2 rounded-full bg-lime-400 text-black sm:bg-transparent sm:text-lime-300 px-4 py-2 sm:p-0 text-[14px] sm:text-[13px] font-bold">Pay {money(p)}</span>
          </button>
        );
      })}
    </div>
  );
}

/** "Free for 10 minutes, then ₹200 a week, ₹400 a month or ₹3,500 a year": the same prices as the cards, in a sentence. */
export function PracticePriceLine({ className = "" }) {
  const [d, setD] = useState(null);
  useEffect(() => { getPracticePlans().then(setD).catch(() => {}); }, []);
  if (!d) return null;
  const parts = d.plans.map((p) => `${money(p)} for ${p.label}`);
  const list = parts.length > 1 ? `${parts.slice(0, -1).join(", ")} or ${parts[parts.length - 1]}` : parts[0];
  return <p className={className}>Free for {Math.round(d.trial_seconds / 60)} minutes with a free account. Then {list}: one payment, no auto-renewal{d.provider === "dodo" ? ", tax included" : ""}.</p>;
}

/** Shown when the free minutes (or a pass) run out, or when someone opens it on purpose ("browse"). */
export default function PracticePaywall({ open, onOpenChange, reason = "trial_over", onPaid, source = "practice" }) {
  useEffect(() => { if (open) track("practice_paywall_viewed", { reason, source }); }, [open, reason, source]);
  const [provider, setProvider] = useState("");
  const [paying, setPaying] = useState(false);   // Razorpay is open: this dialog must not trap focus under it
  useEffect(() => { getPracticePlans().then((d) => setProvider(d.provider || "")).catch(() => {}); }, []);
  const payLine = provider === "paypal" ? "Secure checkout with PayPal." : provider === "dodo" ? "Secure checkout." : "UPI, cards and netbanking.";
  const title = reason === "pass_expired" ? "Your practice pass has ended" : reason === "trial_ending" ? "Keep the coach going" : reason === "browse" ? "Practice passes" : "Your 10 free minutes are used";
  const intro = reason === "browse"
    ? "A pass opens practice for a week, a month or a year, on top of your free minutes. " + payLine + " One payment, no auto-renewal."
    : "Pick a pass to keep practising. " + payLine + " One payment, no auto-renewal.";
  return (
    <Dialog open={open && !paying} onOpenChange={onOpenChange}>
      <DialogContent overlayClassName="z-[79]" className="z-[80] bg-zinc-900 border-zinc-800 sm:max-w-xl max-h-[92dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white text-xl">{title}</DialogTitle>
          <DialogDescription className="text-zinc-400 text-sm">{intro}</DialogDescription>
        </DialogHeader>
        <ul className="space-y-1.5">
          {WHAT_YOU_GET.map((t) => (
            <li key={t} className="flex gap-2 text-[14px] text-zinc-200 leading-snug"><Check className="w-4 h-4 shrink-0 mt-0.5 text-lime-400" strokeWidth={3} />{t}</li>
          ))}
        </ul>
        <PracticePlans source={source} className="mt-2"
          onCheckoutOpen={() => setPaying(true)} onCheckoutEnd={() => setPaying(false)}
          onPaid={(a) => { setPaying(false); onOpenChange(false); onPaid?.(a); }} />
        <p className="flex gap-2 text-[12px] text-zinc-500 leading-snug">
          <ShieldCheck className="w-4 h-4 shrink-0 text-zinc-500" />
          <span>A pass opens practice for its length; buy again while it runs and the days are added on. Payments are processed by {provider === "paypal" ? "PayPal" : provider === "dodo" ? "Dodo Payments" : "Razorpay"}. See the <Link to="/refund" className="underline">refund policy</Link>.</span>
        </p>
      </DialogContent>
    </Dialog>
  );
}

/**
 * A button that opens the pass picker whenever someone wants it, not only once the free minutes are gone.
 * Owns its own dialog, so it can sit on any page. `from` price = the cheapest pass, priced for this visitor.
 */
export function PracticePassButton({ source = "page", label = "Get a practice pass", onPaid, className = "" }) {
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(null);
  useEffect(() => {
    getPracticePlans().then((d) => { const p = [...(d.plans || [])].sort((a, b) => a.price - b.price)[0]; if (p) setFrom(money(p)); }).catch(() => {});
  }, []);
  return (
    <>
      <button type="button" onClick={() => { track("practice_pass_link_tapped", { source }); setOpen(true); }}
        data-testid="practice-pass-link" data-cta="practice_pass_link" className={className}>
        {label}{from ? <span className="font-normal opacity-80"> · from {from}</span> : null}
      </button>
      <PracticePaywall open={open} onOpenChange={setOpen} reason="browse" source={source} onPaid={onPaid} />
    </>
  );
}
